// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  BasketState,
  clearBasketNotification,
} from '@epicuro-next/state/basket-state';

import { eventHandlerFromAction } from '../../../utils/eventhandler-from-action';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../../events';
import {
  errorMessage,
  hasError,
  ifHasNoErrorAndIsNotLoading,
  ifIsLoading,
  ifIsNotLoading,
  isLoading,
  referenceloadingStateWrapper,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import { getPanelTrackingIdProperty } from '../../panel-tracking-id';
import {
  hasData,
  hasNoItems,
  httpBodyToProductData,
  productData,
} from '../../product-data';
import { getServingConfigProperty } from '../../serving-config';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:added-to-basket-notification-root',
  property: 'panelName',
};

const panelStyleProvider: IDynamicBindableValue = {
  provider: 'id:added-to-basket-notification-root',
  property: 'panelTrackingId',
};

const loadingSkeleton: IDynamicItem = {
  id: 'loading-skeleton',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['h-100'],
  },
  inputs: {
    media: {
      url: 'assets/static-skeleton-screen.png',
    },
    objectFit: 'cover',
  },
};

const isLoadingWrapper: IDynamicItem = {
  id: 'is-loading-wrapper',
  contentTypeId: 'dynamic-collections-renderer',
  if: ifIsLoading(),
  children: [
    {
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['col'],
      },
      children: [loadingSkeleton],
    },
  ],
};

const header: IDynamicSimpleText = {
  id: 'header',
  contentTypeId: 'dynamic-elements-simple-text',
  inputs: {
    text: recommendationsPanelsLocaleKeys.similarProductsYouMightLike,
  },
  styles: {
    classes: ['marketing-panel__heading', 'd-block'],
  },
};

const backgroundMedia: IDynamicItem = {
  id: 'background-media',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__figure'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      url: 'https://epicuropumatestimages.epicurotesting.com/images/static/promotions/1b59f5caa20540e6b6796c4ea8387e4b.png?sv=2019-07-07&sr=b&sig=S1CZo2heZlQHjL8xOGDGoRqeFaRLpeP7VMQq7AfNAB8%3D&se=2123-03-27T12%3A18%3A48Z&sp=r&width=1920&mode=max',
    },
    objectFit: 'cover',
  },
};

const itemMediaPreview: IDynamicItem = {
  id: 'item-media-preview',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__item'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      provider: 'self',
      property: 'productData.media',
    },
  },
  eventHandlers: {
    onClick: [
      recommendationItemClickedEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
      openConfigStripEventHandler('self'),
      eventHandlerFromAction(clearBasketNotification),
    ],
    onVisibleOnce: [
      recommendationItemShownEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
    ],
  },
};

const itemsArrayRenderer: IDynamicItem = {
  id: 'items-array-renderer',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: ifHasNoErrorAndIsNotLoading(),
  styles: {
    classes: ['marketing-panel__items', 'justify-content-center'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  data: {
    products: {
      provider: 'parent',
      property: 'productData',
      transform: function (productDataArg: []) {
        return (productDataArg ?? []).slice(0, 3);
      }.toString(),
    },
    loadingStateWrapper: referenceloadingStateWrapper('parent'),
  },
  inputs: {
    propertyToUse: 'productData',
    items$: {
      provider: 'self',
      property: 'products',
    },
    contentType: itemMediaPreview,
  },
};

const errorMessageItem: IDynamicItem = {
  ...errorMessage(),
  styles: {
    classes: [
      'd-flex',
      'w-100',
      'justify-content-center',
      'mb-2',
      'text-muted',
    ],
  },
};

const noItemsItem: IDynamicItem = conditionalMessage(
  'no-items-message',
  'parent',
  'hasNoItems',
  recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
  {
    classes: [
      'd-flex',
      'w-100',
      'justify-content-center',
      'text-muted',
      'mb-2',
    ],
  },
);

const isDoneLoadingWrapper: IDynamicItem = {
  id: 'is-done-loading-wrapper',
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    classes: ['marketing-panel', 'd-block', 'panel-3-items-with-no-media-3'],
    style: {
      margin: '10px',
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  if: ifIsNotLoading(),
  children: [
    header,
    backgroundMedia,
    itemsArrayRenderer,
    errorMessageItem,
    noItemsItem,
  ],
};

export const addedToBasketNotification: IDynamicItem = {
  id: 'added-to-basket-notification-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName: 'ConfigStrip',
    filterMissingImages: false,
    loadingStateWrapper: {
      provider: 'HttpBodyProvider',
      property: {
        values: [
          {
            provider: 'self',
            property: 'panelName',
          },
          {
            provider: 'GlobalStoreProviderService',
            property: {
              once: true,
              paths: 'basket.data.basketNotification',
            },
          },
          {
            provider: 'self',
            property: 'filterMissingImages',
          },
        ],
        transform: function ([
          panelName,
          basketNotification,
          filterMissingImages,
        ]: [
          string,
          Exclude<BasketState['data']['basketNotification'], undefined>,
          boolean,
        ]) {
          const productId =
            basketNotification.materialId ??
            basketNotification.parentId ??
            basketNotification.productId;

          const body = {
            panelType: panelName,
            properties: {
              moreLikeProductId: productId,
              moreLikeBrandId: basketNotification.brandId,
              filterMissingImages,
            },
          };

          function createFetchString(bodyArg: object): string {
            const headersJson = JSON.stringify({
              accept: 'application/json, text/plain, */*',
              'accept-language': 'en-US',
              'content-type': 'application/json',
            });
            const bodyJson = JSON.stringify(JSON.stringify(bodyArg));
            const url = '/api/v2/recommendation/suggest';
            const method = 'POST';
            return `fetch("${url}", { "headers": ${headersJson}, "body": ${bodyJson}, "method": "${method}" });`;
          }

          return createFetchString(body);
        }.toString(),
      },
      transform: httpBodyToProductData.toString(),
      wrapWithLoadingState: true,
    },
    isLoading: isLoading(),
    productData: productData(),
    hasError: hasError(),
    hasData: hasData('self'),
    hasNoItems: hasNoItems(),
    ...getServingConfigProperty(),
    ...getPanelTrackingIdProperty({
      page: 'cs',
      location: 'nt',
      type: '3',
      provider: 'nebp',
      cssVariant: '001',
    }),
  },
  children: [
    {
      id: 'row-wrapper',
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['row'],
      },
      children: [
        isLoadingWrapper,
        {
          id: 'col-wrapper',
          contentTypeId: 'dynamic-collections-renderer',
          styles: {
            classes: ['col-12'],
          },
          children: [isDoneLoadingWrapper],
        },
      ],
    },
  ],
};
